import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CodeViewer } from "./CodeViewer";
import { AlertTriangle, ChevronDown, FileSpreadsheet, X } from "lucide-react";
import { useState } from "react";
import { formatReadmeContent } from "@/lib/formatReadmeContent";
interface VbaModule {
  id: string;
  name: string;
  code: string;
  riskScan: {
    score: string;
    flags: any[];
  } | null;
}
interface ExcelFile {
  id: string;
  name: string;
  size: number;
}
interface SubmissionPreviewProps {
  open: boolean;
  onClose: () => void;
  name: string;
  shortDesc: string;
  installationGuide?: string;
  modules: VbaModule[];
  excelFiles: ExcelFile[];
  semver: string;
  screenshotPreview: string;
  tags: any[];
  selectedTags: number[];
}
export const SubmissionPreview = ({
  open,
  onClose,
  name,
  shortDesc,
  installationGuide,
  modules,
  excelFiles,
  semver,
  screenshotPreview,
  tags,
  selectedTags
}: SubmissionPreviewProps) => {
  const [openModules, setOpenModules] = useState<Set<string>>(new Set([modules[0]?.id]));
  const toggleModule = (moduleId: string) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };
  const getRiskBadgeVariant = (score: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (score) {
      case "high":
        return "destructive";
      case "medium":
        return "outline";
      case "low":
        return "secondary";
      default:
        return "secondary";
    }
  };
  const selectedTagNames = tags.filter(tag => selectedTags.includes(tag.id));
  return <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <DialogTitle className="text-2xl">Preview Submission</DialogTitle>
            <div className="flex gap-2 ml-auto">
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-8 lg:space-y-12">
          {/* Preview Image */}
          {screenshotPreview && <div className="w-full aspect-video rounded-2xl overflow-hidden border border-border/30 shadow-sm hover:shadow-md transition-shadow">
              <img src={screenshotPreview} alt={name} className="w-full h-full object-cover" />
            </div>}

          {/* Hero Section */}
          <div className="space-y-6">
            <div className="space-y-5">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-tight">{name || "Untitled Macro"}</h1>

              <p className="text-lg text-foreground/80 leading-relaxed italic">
                {shortDesc || "No description provided"}
              </p>

              {/* Category Tags */}
              <div className="flex flex-wrap gap-2">
                {selectedTagNames.map(tag => <Badge key={tag.id} variant="secondary" className="text-sm">
                    {tag.name}
                  </Badge>)}
              </div>
            </div>
          </div>

          {/* VBA Solution Section */}
          <section className="space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">VBA Solution</h2>

            <div className="space-y-4">
              {modules.map((module, idx) => {
              const moduleColor = idx % 3 === 0 ? "border-l-primary" : idx % 3 === 1 ? "border-l-secondary" : "border-l-accent";
              const previewLines = module.code.split("\n").slice(0, 3).join("\n");
              return <Collapsible key={module.id} open={openModules.has(module.id)} onOpenChange={() => toggleModule(module.id)}>
                    <Card className={`shadow-sm overflow-hidden transition-all hover:shadow-md border-l-[3px] ${moduleColor} rounded-2xl border-border/50`}>
                      <CollapsibleTrigger className="w-full">
                        <CardHeader className="pb-2 cursor-pointer hover:bg-muted/20 transition-colors p-2 lg:p-3 border-b-0">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 lg:gap-4 text-left flex-1 min-w-0">
                              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform flex-shrink-0 ${openModules.has(module.id) ? "rotate-180" : ""}`} />
                              <div className="min-w-0 flex-1">
                                <CardTitle className="text-lg lg:text-xl font-bold">
                                  {module.name || "Untitled"}
                                </CardTitle>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <CardContent className="space-y-3 pt-0 pb-3 px-0 relative">
                          <div className="overflow-hidden relative" style={{ height: 250 }}>
                            <CodeViewer code={module.code || "' No code provided"} height="250px" />
                          </div>

                          {module.riskScan?.flags && module.riskScan.flags.length > 0 && <div className="p-4 lg:p-5 bg-destructive/5 rounded-xl border border-destructive/20 mx-3">
                              <h4 className="font-semibold mb-3 text-destructive flex items-center gap-2 text-sm">
                                <AlertTriangle className="h-4 w-4" />
                                Security Flags
                              </h4>
                              <ul className="space-y-2 text-xs">
                                {module.riskScan.flags.map((flag: any, idx: number) => <li key={idx} className="text-destructive/90 font-mono">
                                    Line {flag.line}: {flag.type} - {flag.snippet}
                                  </li>)}
                              </ul>
                            </div>}

                          <div className="flex gap-2 flex-wrap pt-1 px-3">
                            <Button variant="outline" size="sm" disabled className="shadow-sm border-border/50">
                              Copy
                            </Button>
                            <Button variant="outline" size="sm" disabled className="shadow-sm border-border/50">
                              Download .bas
                            </Button>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>;
            })}
            </div>
          </section>

          {/* Excel Attachments within VBA Solution */}
          {excelFiles.length > 0 && <div className="space-y-4 mt-6">
              {excelFiles.map((file, idx) => {
              const fileColor = idx % 3 === 0 ? "border-l-primary" : idx % 3 === 1 ? "border-l-secondary" : "border-l-accent";
              return <Card key={idx} className={`shadow-sm overflow-hidden border-l-[3px] ${fileColor} rounded-2xl border-border/50`}>
                  <CardContent className="p-4 lg:p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="h-8 w-8 text-success flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" disabled className="shadow-sm border-border/50 flex-shrink-0">
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>;
            })}
            </div>}

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <Button onClick={onClose} size="lg">
              Close Preview
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};